// Sekme şeridinin oturum ömrü (SEM-8116).
//
// Şerit sessionStorage'da yaşar ve çıkış yönlendirmesi aynı tarayıcı
// sekmesinde kaldığı için kendiliğinden silinmez. Buradaki iki yol onu
// siler: kullanıcının bastığı çıkış, ve çıkış olmadan değişen özne.

import { useRef } from "react";
import { useAuth, type AuthContextProps } from "react-oidc-context";
import type { QueryClient } from "@tanstack/react-query";

import { isDevAuthActive, signOutDevAuth } from "@/auth/dev-auth";
import { clearTabs } from "@/store/tabs";
import { setAccessToken } from "@/api/platform-graph";
import { sessionTermination, type SessionTermination } from "./session-termination";

/** Son görülen oturum öznesi (OIDC `sub`). Sayfa yenilemesini aşmalı. */
const SESSION_SUBJECT_STORAGE_KEY = "novemai.sessionSubject";

/**
 * Çıkış: önce oturuma ait yerel durumu siler, sonra IdP'ye yönlendirir.
 * Silme yönlendirmeden ÖNCEDİR — yönlendirme bu sayfayı terk eder ve
 * sonrasında koşacak kod kalmaz.
 */
export function signOut(
  auth: Pick<AuthContextProps, "signoutRedirect" | "stopSilentRenew" | "user">,
  client: QueryClient,
  termination: SessionTermination = sessionTermination,
): void {
  clearTabs();
  if (isDevAuthActive()) {
    signOutDevAuth();
    return;
  }
  // Kullanıcı deposu kapanınca UserManager bu ipucunu okuyamaz. Önce yakala;
  // yalnız bellekte kalan bu değer end_session isteğinin mevcut sözleşmesidir.
  const idTokenHint = auth.user?.id_token;
  termination.begin();
  auth.stopSilentRenew();
  setAccessToken(null);
  void client.cancelQueries().catch(() => {});
  client.clear();
  const attempt = (): void => {
    void auth.signoutRedirect(idTokenHint ? { id_token_hint: idTokenHint } : undefined)
      .then(result => {
        // react-oidc-context navigator reddini yakalar ve null döndürür.
        if (result === null) termination.failSignOut(attempt);
      }, () => { termination.failSignOut(attempt); });
  };
  // Burada finish çağrılmaz: normal çıkışın sahibi end_session yönlendirmesi,
  // RequireAuth'ın DELETE sonrası yeniden giriş yönlendirmesi değildir.
  attempt();
}

/**
 * Özne çıkış olmadan değiştiyse şeridi siler — sessiz bir kullanıcı
 * değişimi öncekinin sekmelerini devralamaz.
 *
 * Silme EFEKTTE değil RENDER sırasında olur (useAuthFetch ile aynı,
 * ref ile sınırlanmış desen): React efektleri çocuktan başlatır, yani
 * TabStrip'in kendi syncLocation efekti kök efektten önce koşar ve önceki
 * kullanıcının listesini yeniden yazardı. Uygulama kökünde bir kez çağrılır.
 */
export function useClearTabsOnSubjectChange(): void {
  const subject = useAuth().user?.profile.sub ?? null;
  const applied = useRef<string | null>(null);

  if (subject !== null && applied.current !== subject) {
    applied.current = subject;
    try {
      const previous = window.sessionStorage.getItem(SESSION_SUBJECT_STORAGE_KEY);
      if (previous !== null && previous !== subject) clearTabs();
      window.sessionStorage.setItem(SESSION_SUBJECT_STORAGE_KEY, subject);
    } catch {
      // Kota dolu ya da depo kapalı. Burası uygulama kökünün render'ı ve
      // üstünde hata sınırı yok: yutulmayan bir hata boş ekran demek.
    }
  }
}
